<template>
    <div class="member-sidebar">
        <!-- 手機版下拉式選單 -->
        <div class="d-block d-md-none mb-3">
            <b-form-select v-model="currentPath" :options="selectOptions" @change="navigateTo"></b-form-select>
        </div>

        <!-- 桌機版手風琴 -->
        <div class="d-none d-md-block">
            <div v-for="group in groups" :key="group.label" class="sidebar-group">
                <div class="sidebar-group-label" @click="toggleGroup(group.label)" role="button">
                    <span>{{ group.label }}</span>
                    <b-icon :icon="openGroups.includes(group.label) ? 'chevron-up' : 'chevron-down'" class="sidebar-chevron"></b-icon>
                </div>
                <b-collapse :visible="openGroups.includes(group.label)">
                    <b-nav vertical pills class="sidebar-nav">
                        <b-nav-item
                            v-for="item in group.items"
                            :key="item.to"
                            :to="item.to"
                            :active="isItemActive(item.to)"
                            exact>
                            {{ item.label }}
                            <b-badge v-if="item.to === '/campOrder' && hasCampTodo" pill variant="danger"
                                class="unpaid-dot ml-1" :title="campTodoTitle">&nbsp;</b-badge>
                            <b-badge v-if="item.to === '/memberNotification' && unreadCount" pill variant="danger" class="ml-1">{{ unreadCount }}</b-badge>
                        </b-nav-item>
                    </b-nav>
                </b-collapse>
            </div>
        </div>
        <b-modal id="member-notification-summary" centered hide-header-close>
            <template #modal-header>
                <h5 class="modal-title">
                    最新未讀通知
                    <b-badge v-if="unreadCount" pill variant="danger" class="ml-1">{{ unreadCount }}</b-badge>
                </h5>
            </template>
            <b-alert v-if="unpaidOrderCount" show variant="warning" class="mb-3">
                <div class="unpaid-order-alert">
                    <span>目前有 <strong>{{ unpaidOrderCount }}</strong> 筆待付款營隊訂單。</span>
                    <b-button size="sm" variant="outline-dark" @click="goUnpaidCampOrder">
                        {{ unpaidOrderCount === 1 ? '前往付款' : '查看待付款訂單' }}
                    </b-button>
                </div>
            </b-alert>
            <b-list-group v-if="latestUnread.length">
                <b-list-group-item v-for="item in latestUnread" :key="item.Id" button @click="openNotification(item.Id)">
                    <div class="font-weight-bold">{{ item.Title }}</div>
                    <div class="small text-muted text-truncate">{{ item.ContentSummary }}</div>
                </b-list-group-item>
            </b-list-group>
            <div v-else class="text-muted">目前沒有未讀通知。</div>
            <template #modal-footer="{ ok }">
                <b-button variant="outline-secondary" @click="ok()">稍後查看</b-button>
                <b-button variant="primary" @click="goNotificationCenter">前往通知中心</b-button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import { featureFlags } from '@/api/API';

const notificationPopupSessionKey = 'memberNotificationSummaryShown';

export default {
    data() {
        return {
            groups: [
                {
                    label: '一般課程',
                    items: [
                        { to: '/order', label: '訂單' },
                        { to: '/myRegistration', label: '報名表' },
                        { to: '/favorite', label: '課程收藏' },
                    ],
                },
                {
                    label: '營隊',
                    items: [
                        { to: '/campRegistration', label: '報名列表' },
                        { to: '/campOrder', label: '訂單總覽' },
                    
                        // 報價單無營隊收藏功能，暫時隱藏
                        // { to: '/campFavorite', label: '營隊收藏' },
                        { to: '/campRelations', label: '家庭成員' },
                        { to: '/campCoupon', label: '折價券' },
                        { to: '/memberNotification', label: '通知中心' },
                    ],
                },
                {
                    label: '帳號',
                    items: [
                        { to: '/profile', label: '基本資料' },
                        { to: '/accountSecurity', label: '帳號安全' },
                    ],
                },
            ],
            openGroups: [],
            notificationTimer: null,
        };
    },
    computed: {
        currentPath() {
            if (this.$route.path.startsWith('/memberNotification/')) return '/memberNotification';
            // 家庭成員新增／編輯頁（/campRelations/new、/campRelations/:id）仍高亮「家庭成員」。
            if (this.$route.path.startsWith('/campRelations/')) return '/campRelations';
            return this.$route.path;
        },
        hasCampTodo() {
            return this.$store.getters['campUnpaidOrder/hasCampTodo'];
        },
        campTodoTitle() {
            const s = this.$store.state.campUnpaidOrder;
            const parts = [];
            if (s.hasUnpaid) parts.push('待付款訂單');
            if (s.hasUnpaidSurcharge) parts.push('補收款待繳');
            if (s.hasPendingRefund) parts.push('退費待處理');
            return parts.length ? `有${parts.join('、')}` : '';
        },
        unreadCount() { return this.$store.state.memberNotification.unreadCount; },
        unpaidOrderCount() { return this.$store.state.memberNotification.unpaidOrderCount; },
        singleUnpaidOrderId() { return this.$store.state.memberNotification.singleUnpaidOrderId; },
        latestUnread() { return this.$store.state.memberNotification.latestUnread; },
        activeGroupLabel() {
            const path = this.currentPath;
            return this.groups.find((g) => g.items.some((item) => item.to === path))?.label;
        },
        selectOptions() {
            return this.groups.map((g) => ({
                label: g.label,
                options: g.items.map((item) => ({
                    value: item.to,
                    text: this.mobileItemText(item),
                })),
            }));
        },
    },
    mounted() {
        this.openGroups = this.groups.map((g) => g.label);
        this.$store.dispatch('campUnpaidOrder/fetchHasUnpaid');
        this.refreshNotifications();
        this.notificationTimer = window.setInterval(() => {
            if (!document.hidden) this.refreshNotifications();
        }, 60000);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.showLoginSummary();
    },
    beforeDestroy() {
        window.clearInterval(this.notificationTimer);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
    },
    methods: {
        isItemActive(path) {
            return this.currentPath === path;
        },
        mobileItemText(item) {
            if (item.to === '/campOrder' && this.hasCampTodo) return `${item.label} ●`;
            if (item.to === '/memberNotification' && this.unreadCount) return `${item.label} (${this.unreadCount})`;
            return item.label;
        },
        refreshNotifications() {
            return this.$store.dispatch('memberNotification/fetchUnreadCount');
        },
        onVisibilityChange() {
            if (!document.hidden) this.refreshNotifications();
        },
        async showLoginSummary() {
            if (!featureFlags.memberNotificationLoginPopupEnabled || sessionStorage.getItem(notificationPopupSessionKey)) return;
            sessionStorage.setItem(notificationPopupSessionKey, 'true');
            try {
                const summary = await this.$store.dispatch('memberNotification/fetchSummary');
                if ((summary.UnpaidOrderCount || 0) > 0 || (summary.UnreadCount || 0) > 0) {
                    this.$bvModal.show('member-notification-summary');
                }
            } catch {
                sessionStorage.removeItem(notificationPopupSessionKey);
            }
        },
        openNotification(id) {
            this.$bvModal.hide('member-notification-summary');
            this._go(`/memberNotification/${id}`);
        },
        goUnpaidCampOrder() {
            this.$bvModal.hide('member-notification-summary');
            const path = this.unpaidOrderCount === 1 && this.singleUnpaidOrderId
                ? `/campOrderDetail?orderId=${encodeURIComponent(this.singleUnpaidOrderId)}`
                : '/campOrder';
            this._go(path);
        },
        goNotificationCenter() {
            this.$bvModal.hide('member-notification-summary');
            this._go('/memberNotification');
        },
        toggleGroup(label) {
            const idx = this.openGroups.indexOf(label);
            if (idx >= 0) {
                this.openGroups.splice(idx, 1);
            } else {
                this.openGroups.push(label);
            }
        },
        navigateTo(path) {
            if (path && path !== this.$route.path) {
                this._go(path);
            }
        },
    },
};
</script>
<style scoped>
.unpaid-order-alert {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

@media (max-width: 575.98px) {
    .unpaid-order-alert {
        align-items: stretch;
        flex-direction: column;
    }
}

.sidebar-group {
    margin-bottom: 0.25rem;
}
.sidebar-group-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.95rem;
    font-weight: 600;
    color: #343a40;
    padding: 0.4rem 0.75rem;
    border-radius: 0.375rem;
    cursor: pointer;
    user-select: none;
}
.sidebar-group-label:hover {
    background-color: #e2e6ea;
}
.sidebar-chevron {
    font-size: 0.75rem;
    color: #6c757d;
}
.sidebar-nav {
    padding-left: 0.5rem;
}
.sidebar-nav .nav-link {
    font-size: 0.85rem;
    color: #495057;
    padding: 0.3rem 0.75rem;
}
.sidebar-nav .nav-link:hover {
    color: #343a40;
}
.unpaid-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    padding: 0;
    line-height: 6px;
    overflow: hidden;
    vertical-align: middle;
}
</style>
